import { DECK_RECIPES, recipeStats, validateRecipe, type DeckRecipeId } from './recipes';

export type TrumpMode = 'CUT' | 'BAND_A_AUTO' | 'BAND_B_REQUEST' | 'RANDOM_OPEN';
export type TiePolicy = 'LAST_PLAYED' | 'FIRST_PLAYED';
export type DealerPolicy = 'LOSER_DEALS' | 'ROTATE';
export type ScoreModel = 'HAND_WINS' | 'APP_POINTS';
export type PlayerCount = 4 | 6 | 8;

export interface RulesConfig {
  playerCount: PlayerCount;
  deckRecipe: DeckRecipeId;
  trumpMode: TrumpMode;
  duplicateTie: TiePolicy;
  dealerPolicy: DealerPolicy;
  scoreModel: ScoreModel;
  /** Match ends when a team reaches this many hand wins (HAND_WINS) or points (APP_POINTS). */
  target: number;
  /** Seconds per turn; 0 disables the turn timer (practice mode). */
  actionTimeoutSec: number;
}

/** P0 defaults from PRD §11: Cut trump, hand-win scoring, LAST_PLAYED ties, loser deals. */
export const DEFAULT_RULES: Readonly<RulesConfig> = {
  playerCount: 4,
  deckRecipe: 'CLASSIC_4',
  trumpMode: 'CUT',
  duplicateTie: 'LAST_PLAYED',
  dealerPolicy: 'LOSER_DEALS',
  scoreModel: 'HAND_WINS',
  target: 3,
  actionTimeoutSec: 30,
};

export const TRUMP_MODES: readonly TrumpMode[] = [
  'CUT',
  'BAND_A_AUTO',
  'BAND_B_REQUEST',
  'RANDOM_OPEN',
];
export const P0_TRUMP_MODES: readonly TrumpMode[] = ['CUT', 'BAND_A_AUTO'];

export const DEFAULT_RECIPE_FOR: Record<PlayerCount, DeckRecipeId> = {
  4: 'CLASSIC_4',
  6: 'DOUBLE_6',
  8: 'DOUBLE_8',
};

export function makeRules(overrides: Partial<RulesConfig> = {}): RulesConfig {
  const playerCount = overrides.playerCount ?? DEFAULT_RULES.playerCount;
  return {
    ...DEFAULT_RULES,
    deckRecipe: DEFAULT_RECIPE_FOR[playerCount],
    dealerPolicy: playerCount === 4 ? 'LOSER_DEALS' : 'ROTATE',
    ...overrides,
  };
}

export function validateRules(rules: RulesConfig): string[] {
  const errors: string[] = [];
  const recipe = DECK_RECIPES[rules.deckRecipe];
  if (!recipe) return [`Unknown deck recipe ${rules.deckRecipe}`];
  if (recipe.playerCount !== rules.playerCount) {
    errors.push(
      `${rules.deckRecipe} is for ${recipe.playerCount} players, not ${rules.playerCount}`,
    );
  }
  errors.push(...validateRecipe(recipe));
  if (!TRUMP_MODES.includes(rules.trumpMode)) errors.push(`Unknown trump mode ${rules.trumpMode}`);
  if (!Number.isInteger(rules.target) || rules.target < 1 || rules.target > 50) {
    errors.push('Target must be an integer between 1 and 50');
  }
  if (
    !Number.isInteger(rules.actionTimeoutSec) ||
    rules.actionTimeoutSec < 0 ||
    rules.actionTimeoutSec > 300
  ) {
    errors.push('Turn timeout must be between 0 and 300 seconds');
  }
  if (recipeStats(recipe).tens === 0) errors.push(`${rules.deckRecipe} has no tens`);
  return errors;
}

export function rulesStats(rules: RulesConfig) {
  return recipeStats(DECK_RECIPES[rules.deckRecipe]);
}
